import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VisuallyHidden,
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon } from "@chakra-ui/icons";
import { useIntl } from "react-intl";
import { usePortCheck } from "hooks/api/use-port-check";
import type {
  PortCheckBlockProps,
  PortCheckError,
  PortGroup,
  PortResult,
  PortState,
} from "./types";

/**
 * Open-port check block, rendered under the IP check result.
 *
 * Spec: specs/active/2026-10-14-open-port-check (US1, US2, §5, plan step 15).
 * Design: docs/design/design-system.md — semantic tokens only; the primary
 * fill is `brand.solid` (color.primary.strong) because the label is
 * normal-size; an open port is flagged by icon + word + colour, never colour
 * alone.
 */

const GROUPS: PortGroup[] = ["service", "proxy", "vpn"];

const STATE_RANK: Record<PortState, number> = { open: 0, filtered: 1, closed: 2 };

/** Open first, then filtered, then closed; by port inside each state. The
 *  server already sorts this way — re-sorting keeps the UI honest if it
 *  ever does not. */
const byStateThenPort = (a: PortResult, b: PortResult) =>
  STATE_RANK[a.state] - STATE_RANK[b.state] || a.port - b.port;

const minutesSince = (iso: string): number => {
  const t = Date.parse(iso);
  if (isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
};

const StateBadge = ({ state }: { state: PortState }) => {
  const intl = useIntl();
  const label = intl.formatMessage({
    id: `portCheck.state.${state}`,
    defaultMessage: state,
  });
  if (state === "open") {
    return (
      <Badge
        display="inline-flex"
        alignItems="center"
        gap={1}
        px={2}
        borderRadius="sm"
        borderWidth="1px"
        borderColor="status.error"
        bg="status.errorSubtle"
        color="status.error"
        textTransform="none"
        fontSize="sm"
      >
        <WarningIcon aria-hidden="true" boxSize={3} />
        {label}
      </Badge>
    );
  }
  return (
    <Badge
      px={2}
      borderRadius="sm"
      bg="neutral.100"
      color="text.secondary"
      textTransform="none"
      fontSize="sm"
      fontWeight="normal"
    >
      {label}
    </Badge>
  );
};

const PortGroupTable = ({
  group,
  ports,
  expanded,
}: {
  group: PortGroup;
  ports: PortResult[];
  expanded: boolean;
}) => {
  const intl = useIntl();
  const visible = expanded ? ports : ports.filter((p) => p.state === "open");
  const title = intl.formatMessage({ id: `portCheck.group.${group}` });

  return (
    <Box as="section" aria-label={title}>
      <Heading as="h3" fontSize="md" fontWeight="semibold" mb={2} color="text.primary">
        {title}
      </Heading>
      {visible.length === 0 ? (
        <Text fontSize="sm" color="text.secondary">
          {intl.formatMessage({ id: "portCheck.groupNoneOpen" })}
        </Text>
      ) : (
        <TableContainer
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          whiteSpace="normal"
        >
          {/* Fixed layout + the same column widths in every group, so the
              Services / Proxies / VPN tables line up. */}
          <Table size="sm" layout="fixed">
            <TableCaption placement="top" m={0} p={0}>
              <VisuallyHidden>
                {intl.formatMessage({ id: "portCheck.tableCaption" }, { group: title })}
              </VisuallyHidden>
            </TableCaption>
            {/* Below md the caps header loses its letter spacing and half its side
                padding: at 375px "СОСТОЯНИЕ" otherwise broke mid-word in the
                7rem state column (design review 2026-10-30). */}
            <Thead bg="neutral.50">
              <Tr>
                <Th
                  color="text.secondary"
                  borderColor="border.subtle"
                  isNumeric
                  w={20}
                  px={{ base: 2, md: 4 }}
                  letterSpacing={{ base: "normal", md: "wider" }}
                >
                  {intl.formatMessage({ id: "portCheck.col.port" })}
                </Th>
                <Th
                  color="text.secondary"
                  borderColor="border.subtle"
                  px={{ base: 2, md: 4 }}
                  letterSpacing={{ base: "normal", md: "wider" }}
                >
                  {intl.formatMessage({ id: "portCheck.col.service" })}
                </Th>
                <Th
                  color="text.secondary"
                  borderColor="border.subtle"
                  w={28}
                  px={{ base: 2, md: 4 }}
                  letterSpacing={{ base: "normal", md: "wider" }}
                  whiteSpace="nowrap"
                >
                  {intl.formatMessage({ id: "portCheck.col.state" })}
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {visible.map((p) => {
                const isOpen = p.state === "open";
                return (
                  <Tr
                    key={p.port}
                    data-testid={`port-row-${p.port}`}
                    data-state={p.state}
                    bg={isOpen ? "status.errorSubtle" : undefined}
                  >
                    <Td
                      fontFamily="mono"
                      isNumeric
                      borderColor="border.subtle"
                      color="text.primary"
                      verticalAlign="top"
                    >
                      {p.port}
                    </Td>
                    <Td borderColor="border.subtle" color="text.primary" verticalAlign="top">
                      <Text fontSize="sm" fontWeight={isOpen ? "semibold" : "normal"}>
                        {p.service}
                      </Text>
                      {isOpen && (
                        <Text fontSize="sm" color="text.primary" mt={1}>
                          {intl.formatMessage({
                            id: `portCheck.note.${p.port}`,
                            defaultMessage: intl.formatMessage({
                              id: "portCheck.note.default",
                            }),
                          })}
                        </Text>
                      )}
                    </Td>
                    <Td borderColor="border.subtle" verticalAlign="top">
                      <StateBadge state={p.state} />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const ErrorMessage = ({ error }: { error: PortCheckError }) => {
  const intl = useIntl();
  let text: string;
  if (error.reason === "rate_limited") {
    const secs = error.retryAfter;
    if (secs) {
      const at = intl.formatTime(new Date(Date.now() + secs * 1000), {
        hour: "2-digit",
        minute: "2-digit",
      });
      text =
        secs < 60
          ? intl.formatMessage({ id: "portCheck.error.rateLimitedSeconds" }, { seconds: secs, at })
          : intl.formatMessage(
              { id: "portCheck.error.rateLimitedMinutes" },
              { minutes: Math.ceil(secs / 60), at }
            );
    } else {
      text = intl.formatMessage({ id: "portCheck.error.rateLimited" });
    }
  } else {
    text = intl.formatMessage({ id: `portCheck.error.${error.reason}` });
  }
  return (
    <Alert
      status="error"
      variant="left-accent"
      borderRadius="md"
      bg="status.errorSubtle"
      color="text.primary"
      borderColor="status.error"
      data-testid="port-check-error"
    >
      <AlertIcon color="status.error" />
      <AlertDescription fontSize="sm">{text}</AlertDescription>
    </Alert>
  );
};

const PortCheckBlock = ({
  ip,
  apiToken,
  isPaid = false,
  onCharged,
}: PortCheckBlockProps) => {
  const intl = useIntl();
  // No cost on the label -> no credentials on the wire, so an unpriced
  // button can never spend a request.
  const { data, error, loading, run } = usePortCheck(isPaid ? apiToken : undefined);
  const [expanded, setExpanded] = useState(false);

  // While loading the button is disabled, and a disabled element drops focus to
  // <body>: a keyboard or screen-reader user pressing it landed back at the top
  // of the document (design review 2026-10-30). Return focus to the button,
  // which now reads "check again", once the outcome is on screen — but only if
  // focus was actually lost, never stealing it from somewhere the user moved.
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wasLoading = useRef(false);
  useEffect(() => {
    if (wasLoading.current && !loading) {
      const active = document.activeElement;
      if (!active || active === document.body) buttonRef.current?.focus();
    }
    wasLoading.current = loading;
  }, [loading]);

  useEffect(() => {
    if (data?.charged && onCharged) onCharged();
    // Fire once per result, not on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const grouped = useMemo(() => {
    const out: Record<PortGroup, PortResult[]> = { service: [], proxy: [], vpn: [] };
    (data?.ports || []).forEach((p) => {
      if (out[p.group]) out[p.group].push(p);
    });
    GROUPS.forEach((g) => out[g].sort(byStateThenPort));
    return out;
  }, [data]);

  const total = data?.ports.length ?? 0;
  const openCount = data ? data.ports.filter((p) => p.state === "open").length : 0;
  const hiddenCount = total - openCount;

  const hasResult = Boolean(data);
  const buttonLabel = intl.formatMessage({
    id: hasResult
      ? isPaid
        ? "portCheck.buttonAgainPaid"
        : "portCheck.buttonAgain"
      : isPaid
      ? "portCheck.buttonPaid"
      : "portCheck.button",
  });
  const loadingLabel = intl.formatMessage({ id: "portCheck.loading" });
  const summary = data
    ? intl.formatMessage({ id: "portCheck.summary" }, { open: openCount, total })
    : "";

  const checkedAt = data
    ? intl.formatDate(data.checked_at, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <Box
      as="section"
      aria-labelledby="port-check-heading"
      mt={6}
      p={{ base: 4, md: 6 }}
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="lg"
      boxShadow="sm"
      width="100%"
      maxWidth="4xl"
      mx="auto"
      data-testid="port-check-block"
    >
      <Heading
        as="h2"
        id="port-check-heading"
        fontSize="xl"
        fontWeight="semibold"
        color="text.primary"
        mb={2}
      >
        {intl.formatMessage({ id: "portCheck.title" })}
      </Heading>
      <Text fontSize="sm" color="text.secondary" mb={4}>
        {intl.formatMessage({ id: "portCheck.intro" })}
      </Text>

      <Button
        ref={buttonRef}
        colorScheme="blue"
        onClick={() => run(ip)}
        isDisabled={loading}
        isLoading={loading}
        loadingText={loadingLabel}
        aria-busy={loading}
        minH={12}
        whiteSpace="normal"
        _focusVisible={{ outline: "2px solid", outlineColor: "brand.accent", outlineOffset: "2px" }}
      >
        {buttonLabel}
      </Button>

      {/* One polite live region: progress, then the outcome summary. Errors
          announce themselves through the Alert's own role="alert". */}
      <Box role="status" aria-live="polite">
        {loading ? (
          <Text fontSize="sm" color="text.secondary" mt={3}>
            {intl.formatMessage({ id: "portCheck.progress" })}
          </Text>
        ) : data ? (
          <VisuallyHidden>{summary}</VisuallyHidden>
        ) : null}
      </Box>

      {error && !loading && (
        <Box mt={4}>
          <ErrorMessage error={error} />
        </Box>
      )}

      {data && !loading && (
        <Stack spacing={6} mt={6} data-testid="port-check-result">
          <Box>
            <Flex align="center" gap={2}>
              {openCount > 0 ? (
                <WarningIcon aria-hidden="true" color="status.error" boxSize={5} />
              ) : (
                <CheckCircleIcon aria-hidden="true" color="status.success" boxSize={5} />
              )}
              <Text
                fontSize="lg"
                fontWeight="semibold"
                color={openCount > 0 ? "status.error" : "status.success"}
                aria-hidden="true"
              >
                {summary}
              </Text>
            </Flex>
            <Text fontSize="sm" color="text.secondary" mt={1}>
              {intl.formatMessage(
                { id: "portCheck.checkedAt" },
                { ip: data.ip, time: checkedAt }
              )}
            </Text>
            {data.cached && (
              <Text fontSize="sm" color="text.secondary" mt={1}>
                {intl.formatMessage(
                  { id: "portCheck.cached" },
                  { minutes: minutesSince(data.checked_at) }
                )}
              </Text>
            )}
            {data.charged && (
              <Text fontSize="sm" color="text.secondary" mt={1}>
                {intl.formatMessage({ id: "portCheck.charged" })}
              </Text>
            )}
          </Box>

          {GROUPS.map((g) => (
            <Box key={g}>
              <PortGroupTable group={g} ports={grouped[g]} expanded={expanded} />
              {/* The fixed "UDP VPNs are not checked" caveat is documented
                  once, not repeated on every result (owner, 2026-10-30). */}
              {g === "vpn" &&
                (data.notes || [])
                  .filter((note) => !/udp/i.test(note))
                  .map((note, i) => (
                    <Text
                      key={i}
                      fontSize="sm"
                      color="text.secondary"
                      mt={2}
                      data-testid="port-check-note"
                    >
                      {note}
                    </Text>
                  ))}
            </Box>
          ))}

          {hiddenCount > 0 && (
            <Box>
              <Button
                variant="outline"
                size="sm"
                minH={12}
                color="text.primary"
                borderColor="border.strong"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
                _focusVisible={{
                  outline: "2px solid",
                  outlineColor: "brand.accent",
                  outlineOffset: "2px",
                }}
              >
                {intl.formatMessage(
                  { id: expanded ? "portCheck.collapse" : "portCheck.expand" },
                  { count: hiddenCount }
                )}
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default PortCheckBlock;
